/* ------------------- IMPORTS */
import styled from '@emotion/styled'
import { Box, Icons } from 'components'
import { Metadata } from 'components/CreatePost/PreviewComponents/BasicComponents/BasicContentComponents/GenericComponents/Metadata/Metadata'
import { Utm } from 'components/CreatePost/PreviewComponents/BasicComponents/BasicContentComponents/GenericComponents/Utm/Utm'
import { openTemplateModal } from 'components/Modal/ModalManager'
import { IPostVariant } from 'types/PostInterface'
import { AppStore, getActivePost, T } from 'utils'
import { extractAndCompareUrls, getActiveText, updatePost } from 'utils/PostUtils/Post'
import { canShowMetadata, getLinkProperties, onPasteLink } from 'utils/PostUtils/ShortUrl'
import { ExtraTitle } from './ExtraTitle'
import { useRef, useState } from 'react'
import { useCreatePostStore } from 'stores/CreatePostStore'

/* ------------------- INTERFACES  */
interface Props {
  post: IPostVariant
}

export const FirstComment = ({ post }: Props) => {
  /* ----------------- VARIABLES  */
  const { variantId, data, info } = post
  const inputRef = useRef<any>(null)
  const showMetadata = canShowMetadata(post)

  const [firstCommentDisabled, setFirstCommentDisabled] = useState(false)
  const showUtm = getLinkProperties(post)!.length > 0

  return (
    <FirstCommentContainer>
      <ExtraTitle text={T.postPage.extraFirstComment} />

      <TextAreaContainer>
        <ProfileImg src={info.account.picture} />

        <TextArea
          ref={inputRef}
          disabled={firstCommentDisabled}
          style={{ opacity: firstCommentDisabled ? 0.5 : 1 }}
          value={data.firstComment?.text}
          onChange={(e) => {
            const description = e.target.value
            const activeText = post.data.firstComment?.text ?? ''

            updatePost(post.variantId, { firstComment: { ...post?.data?.firstComment, text: e.target.value } })
            // if (description.length > activeText.length + 1)
            //   onPasteLink({ activePost: post, newText: description, firstComment: true })

            const urls = (useCreatePostStore.getState().activePost as any).data?.firstComment?.urls

            // Update the urls in the active post
            const newUrls = extractAndCompareUrls(activeText, urls)

            updatePost(post.variantId, {
              firstComment: { ...post?.data?.firstComment, urls: newUrls, text: e.target.value },
            })
          }}
          onPaste={async (e) => {
            e.stopPropagation()
            setFirstCommentDisabled(true)
            const activeText = post.data.firstComment?.text ?? ''
            const pastedText = e.clipboardData.getData('text')
            const start = inputRef?.current?.selectionStart
            const end = inputRef?.current?.selectionEnd

            const beforePaste = activeText?.substring(0, start)
            const afterPaste = activeText?.substring(end)

            const processedText = pastedText
            const newValue = beforePaste + processedText + afterPaste

            await onPasteLink({ activePost: post, newText: pastedText, finalText: newValue, firstComment: true })
            setFirstCommentDisabled(false)
          }}
        />

        <TemplatesIconContainer onClick={() => openTemplateModal(variantId)}>
          <Icons.template />
        </TemplatesIconContainer>
      </TextAreaContainer>

      {/* Metadata */}
      {showMetadata && <Metadata post={post} firstComment />}

      {/* UTM */}
      {showUtm && <Utm post={post} firstComment />}
    </FirstCommentContainer>
  )
}

const FirstCommentContainer = styled(Box)`
  width: 100%;
  display: flex;
  gap: 16px;
  flex-direction: column;
`

const TextAreaContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 10px;
  display: flex;
  padding: 16px 24px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 14px;
  border: 1px dashed ${() => AppStore.theme.o.darkGrey};
  background: ${() => AppStore.theme.o.lightGrey};
`

const ProfileImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 40px;
  background-color: transparent;
  border: none;
  border-bottom: 1px solid ${() => AppStore.theme.o.darkGrey};
  color: ${() => AppStore.theme.o.black};
`

const TemplatesIconContainer = styled(Box)`
  cursor: pointer;
  width: 26px;
  height: 26px;

  svg {
    transition: 300ms ease-in-out;
    fill: ${() => AppStore.theme.o.darkGrey};
  }

  &:hover {
    svg {
      fill: ${() => AppStore.theme.o.grey};
    }
  }
`
